import React from 'react'

import * as GeoJSON from 'geojson'

import { SaveableAssetMapContext } from '@/pages/assets/map/MapContext'

export type IsoDate = string

interface EntityRestrictedParams {
  entityIds: number[]
}

interface TimeRestrictedParams {
  from?: Date
  to?: Date
}

export interface AlarmCountByEntityParams extends EntityRestrictedParams, TimeRestrictedParams {
  severity?: number
  code?: number
}

export interface EntityNumberPair {
  entity: Entity
  value: number
}

export interface ActiveAlarmsBySeverityParams {
  slug: string
  companyId: string
  severity?: number
  code?: string
}

export interface AverageMonitoringPointTemperatureParams extends TimeRestrictedParams {
  slug: string
  companyId: string
  assetId: string
}

export type InsightCardWidth = 'tiny' | 'mini' | 'small' | 'medium' | 'large' | 'full'

// Responsive system types
export type CardSize = InsightCardWidth
export type BreakpointKey = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
//* Dirty move seperating breakpoints,can come back to this to centeralize it
export type CustomGridBreakpointKey = 'sm' | 'md' | 'lg'

export type BreakpointConfig = {
  compressAt: CardSize | 'none' // Which size and smaller become narrow
  tiny: number // columns out of 24
  mini: number
  small: number
  medium: number
  large: number
  full: number
}

export type ResponsiveConfig = Record<BreakpointKey, BreakpointConfig>

// export type InsightParameterValueType = undefined | string | number | boolean | Date | Entity
export type InsightParameterValueType = undefined | string | number | boolean | Date | Entity
export type InsightParametersType = {
  [key: string]:
    | InsightParameterValueType
    | InsightParameterValueType[] /* | InsightParametersType | InsightParametersType[] */
}

export interface InsightData<T> {
  data: T
}

// Widgets used to configure the Insights
type Controls =
  | 'AlarmCodeDropdown'
  | 'AssetDropdownMulti'
  | 'AssetDropdownSingle'
  | 'FacilityDropdownSingle'
  | 'AssetMakeDropdown'
  | 'CompanyDropdown'
  | 'Custom'
  | 'CustomMultiSelect'
  | 'CustomSingleSelect'
  | 'DateSelect'
  | 'DateTimeRange'
  | 'Label'
  | 'SeverityDropdown'
  | 'AlertNameDropdown'
  | 'GeofenceDropdown'
  | 'GeofenceCategoryDropdown'
  | 'CustomNumberInput'
  | 'SpeedInput'
  | 'TemperatureInput'
  | 'TimeNumberInput'

export enum AssetTypeFilter {
  All = 'all',
  InTransit = 'inTransit',
  InStore = 'inStore',
}
export interface InsightConfigControl<P> {
  id: keyof P
  control: Controls
  required?: boolean
  label?: string
  acknowledgementOnly?: boolean
  disableUserSubscriptionFilter?: boolean
  options?: { label: string; value: string; isDefault?: boolean }[]
  // for a one-off custom widget that's not worth creating a module for
  render?: (parameters: P, onChange: (params: P) => void) => React.ReactNode
  help?: string
  placeholder?: string
  isMultiSelect?: boolean
  defaultValue?: string | number | boolean
  showDefault?: boolean
  dependsOn?: string
  dependsOnValue?: string
  showCode?: boolean
  min?: number
  max?: number
  showTime?: boolean
  /** AssetDropdownMulti only: show a "Select all" option in the menu. Defaults to off. */
  includeSelectAll?: boolean
  assetType?: AssetTypeFilter
  /** Menu open direction for select-style controls. 'top' is useful for controls low
   *  in a tall config panel so the menu opens upward instead of off the bottom edge.
   *  Defaults to 'auto'. */
  menuPlacement?: 'auto' | 'top' | 'bottom'
  //! Remember to remove when needed
  autofill?: string
}

export enum InsightStatusCode {
  // initial status when page is loading
  STARTING = 'starting',
  // status when isValid indicates the Insight is not configured
  NOT_CONFIGURED = 'not_configured',
  // the Insight is currently loading data from the api/server
  LOADING = 'loading',
  // no data was found
  NO_DATA = 'no_data',
  // data was found and is ready to be displayed
  DISPLAYING = 'displaying',
  // an error occurred somewhere
  ERROR = 'error',
}

// message is optional to show additional information, mainly when
// the status is NOT_CONFIGURED or ERROR
export type InsightStatus = { code: InsightStatusCode; message?: string }

// Check if a return value is an InsightStatus
export const isInsightStatus = (value: unknown): value is InsightStatus => {
  return (
    typeof (value as InsightStatus).code === 'string' &&
    Object.values(InsightStatusCode).includes((value as InsightStatus).code)
  )
}

interface InsightResultParams<D> {
  configuration: InsightConfigurationType
  status: InsightStatus
  data?: D
}

/** A table as plain strings and numbers, ready for a spreadsheet. */
export interface ExcelTable {
  headers: string[]
  rows: Record<string, string | number>[]
}

export interface InsightDefinition<P, D> {
  // for now, just leave it out of the insights list if it's not active
  // maybe someday we will allow them to be turned off externally
  active: boolean
  // unique to each insight
  slug: string
  category:
    | 'reeferAlarm'
    | 'door'
    | 'asset'
    | 'temperature'
    | 'alert'
    | 'usage'
    | 'demo'
    | 'utilization'
    | 'fuel'
    | 'speed'
    | 'report'
  name: string
  description: string
  defaultWidth: InsightCardWidth
  /** Per-breakpoint pixel height for a newly added card, falling back to the grid's
   *  defaults (400/300/250). Read from the definition at render time rather than
   *  persisted, so changes here reach existing dashboards. Only applies until the
   *  user saves a layout — after that the saved position wins. */
  defaultHeight?: Partial<Record<CustomGridBreakpointKey, number>>
  defaultAspectRatio: number
  icon?: string // good luck figuring out a unique icon for each
  refreshInterval?: number

  configuration: {
    // The widgets that are used to configure the insight
    // can be from a pre-defined set, e.g. CompanyDropdown,
    // or CustomDropdown (supply the options), or can be
    // completely custom (use render)
    controls: InsightConfigControl<P>[]
    // Called after a parameter has changed in case you need to change a different
    // parameter, e.g. if you have an either/or situation.
    transform?: ({ parameters, field }: { parameters: P; field: keyof P }) => P
    // Determines if the insight is fully configured. This affects the Apply Changes
    // button and also whether load is called. Return true if valid. If return value
    // is a string, that should be a description of what is remaining; use false if
    // you don't want to display anything, either will disable the button
    isValid: (parameters: P) => boolean | string
    // If the insight has a display configuration
    displayControls?: InsightConfigControl<P>[]
    // If the display is config'ed enough to move on
    isDisplayValid?: (parameters: P) => boolean | string
  }

  // Load the data for the insight
  // Should not be called if no configuration but if so return InsightStatus.NOT_CONFIGURED
  // If no data return InsightStatus.NO_DATA
  // If loading return InsightStatus.LOADING
  // If done loading and has data, return the data
  load: (api: ApiService, parameters: P) => Promise<D | InsightStatus>

  // Optional CSV export function
  csv?: (api: ApiService, parameters: P, title: string) => Promise<void>

  // Optional Excel export of the loaded data, as the Display shows it but without the
  // drilldown and component cells. Used by the asset History tab.
  toExcel?: (data: D, units: 'Imperial' | 'Metric') => ExcelTable

  // Given the data and the configuration, display the insight
  // The status passed in can be used to display:
  //    NOT_CONFIGURED
  //    LOADING
  //    NO_DATA
  // If the Insight wants to handle those statuses, great, return a react node;
  // otherwise, just return the null and the framework will throw up a default
  Display: ({ configuration, status, data }: InsightResultParams<D>) => React.ReactNode

  // Override the caption below the title, e.g. showing what is configured
  Caption?: ({ configuration, status, data }: InsightResultParams<D>) => React.ReactNode

  // Override the footer on the bottom right; defaults to the number of rows in data
  Footer?: ({ configuration, status, data }: InsightResultParams<D>) => React.ReactNode
}

export interface InsightConfigurationType {
  id: string // id is unique across all Insights for a user; usually a sluggered title
  slug: string // slug is the Insight slug like alarms/active-by-severity
  title: string
  shared: boolean
  width: InsightCardWidth
  aspectRatio: number
  parameters: InsightParametersType
  displayParameters: InsightParametersType
  isDrilldown?: boolean
  position?: {
    lg?: { x: number; y: number; w: number; h: number }
    md?: { x: number; y: number; w: number; h: number }
    sm?: { x: number; y: number; w: number; h: number }
  }
}

export interface DashboardType {
  title: string
  slug: string
  parentSlug?: string
  description?: string
  parameters: InsightParametersType
  insights: Array<InsightConfigurationType>
  isDefault: boolean
  icon?: string
  iconColor?: string
}

/** A named saved view on one of the list pages; each page adds its own settings. */
export interface SavedView {
  name: string
  /** Opens when the page loads. At most one per page. */
  isDefault?: boolean
}

/** The pages with saved views in `savedViews` (the asset map keeps its own `assetMaps`). */
export type SavedViewPage = 'trackers' | 'coldStorage' | 'blastFreezer'

/** Temperature Graph layout, the same for every Temperature Graph the user opens. */
export interface HistoryGraphSettings {
  /** Status bars (power, movement, zone mode, alarms) in rows under the graph instead of on it. */
  statusBarsBelow?: boolean
  /** Sections under the graph that show only their header; the rest are expanded. */
  collapsedSections?: string[]
}

export interface SelectedColumn {
  name: string
  displayOrder: number
}

/**
 * The user's Insights profile as returned by GET /app/profiles/me. Only dashboards,
 * assetMaps, assetMapSideBarWidth, trackerMapListWidth, trackerMapColumns, savedViews and historyGraph are
 * honoured when POSTed back; units is server-owned (from the JWT).
 */
export interface ProfileType {
  units?: string
  dashboards: DashboardType[]
  assetMaps: Record<string, SaveableAssetMapContext>
  assetMapSideBarWidth?: number // width of the sidebar in the asset map page
  trackerMapListWidth?: number // width (px) of the list panel on the tracker map page
  trackerMapColumns?: string[] // ordered list of visible optional column ids on the tracker map list
  savedViews?: Partial<Record<SavedViewPage, Record<string, SavedView>>> // per page, keyed by name
  historyGraph?: HistoryGraphSettings
  selectedColumns?: SelectedColumn[] // columns selected by the user in the asset list table
  // Granted by staff on the admin user detail page; lets this user pick Fresh vs Timescale
  // per insight. Read-only here — the server restores it from the stored profile on every
  // save, so posting it back has no effect.
  can_select_data_source?: boolean
}

export interface UserType {
  id: number
  uuid: string
  profile: ProfileType
}

export interface AlarmCountBySeverity {
  company: string
  critical: number
  warning: number
  informational: number
  total: number
}

export interface AlarmCountByAssetMake {
  make: string
  critical: number
  warning: number
  informational: number
  total: number
}

export interface AlarmCountByAsset {
  assetId: number
  assetName: string
  critical: number
  warning: number
  informational: number
  total: number
}

export interface AlarmCountByName {
  name: string
  code: number
  severity: string
  count: number
}

export type SeverityCodeType = '1' | '2' | '3'
export type SeverityNameType = 'informational' | 'warning' | 'critical'
export const SEVERITIES: Record<SeverityCodeType, SeverityNameType> = {
  1: 'informational',
  2: 'warning',
  3: 'critical',
}

/**
 * A dictionary of the permissions required for guarding endpoints in this application.
 */
export enum Permission {
  CompaniesList = 'company:list',
  ReadCustomers = 'customer:read',
  ReadGroups = 'group:read',
  ReadPermissions = 'permission:read',
  ReadUsers = 'user:read',
  ReadRoles = 'role:read',
  WriteCustomers = 'customer:write',
  WriteGroups = 'group:write',
  WritePermissions = 'permision:write',
  WriteUsers = 'user:write',
  WriteRoles = 'role:write',
  DeleteGroups = 'group:delete',
  DeletePermissions = 'permission:delete',
  DeleteRoles = 'role:delete',
  DeleteUsers = 'user:delete',
}

/**
 * The shape of a single GroupPermission object.
 */
export type GroupPermissions = {
  groupId: string
  permissions: string[]
}

/**
 * The shape of a single GroupRole object.
 */

export type GroupRoles = {
  groupId: string
  roles: string[]
}

/*
 * Define all commonly used Cooltrax types here.
 */

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface Marker {}
export type ApiQuery = { [key: string]: undefined | string | string[] | ApiQuery | ApiQuery[] }
export type ApiParams = { [key: string]: string }

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }
// export type ApiBody = JsonValue
export type ApiBody = { [key: string]: string | string[] | ApiBody | ApiBody[] }

export type SimpleApiResponse<T> = T

export interface ApiResponse<T> {
  data?: T
  code: number
  error?: boolean
  message?: string
  hasMore?: boolean
  count?: number
}

export type ExpiresIn = 'session' | `${number}${'s' | 'm' | 'h'}`

export interface RequestConfig {
  path: string
  origin: string
  expiresIn?: ExpiresIn
}

export interface ApiService {
  get<T>(params: RequestConfig): Promise<ApiResponse<T>>
  post<T, B>(params: RequestConfig & { body?: B }): Promise<ApiResponse<T>>
  put<T, B>(params: RequestConfig & { body?: B }): Promise<ApiResponse<T>>
  remove<T>(params: RequestConfig): Promise<ApiResponse<T>>
  simpleGet<T>(params: RequestConfig): Promise<SimpleApiResponse<T>>
  simplePost<T, B>(params: RequestConfig & { body?: B; token?: string }): Promise<SimpleApiResponse<T>>
  /** A raw body (an image) with its own Content-Type instead of JSON. */
  simplePostBinary<T>(params: RequestConfig & { body: Blob; contentType: string }): Promise<SimpleApiResponse<T>>
  simplePut<T, B>(params: RequestConfig & { body?: B }): Promise<SimpleApiResponse<T>>
  simpleRemove<T>(params: RequestConfig): Promise<SimpleApiResponse<T>>
}

export interface LatLng {
  latitude: number
  longitude: number
  address?: string
}

export interface Entity {
  id: number
  name: string
}

export const isEntity = (t: undefined | null | number | string | Date | boolean | Entity): t is Entity =>
  (t as Entity).id !== undefined

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface Profile {}
export interface Company extends Entity {
  parentId?: number
  parentCompany?: Company
  virtualSetpointAccessTypeId?: number
  enabledAlertAcknowledgement?: boolean
  enableReeferFridgeData?: boolean
  /** Display name including the parent hierarchy, e.g. "Bega NSW (Bega/Linfox FMCG)". Provided by
   *  the /company-list endpoint; falls back to a client-side build when absent. */
  fullName?: string
}
export interface Region extends Entity {
  company: Company
}
export interface DistributionCenter extends Entity {
  company: Company
  region?: Region
}
export interface Geofence extends Entity {
  company?: Company
  region?: Region
  distributionCenter?: DistributionCenter
  geo?: GeoJSON.GeoJsonObject
}
export interface FridgeType extends Entity {
  make: string
  model: string
}
export interface Asset extends Entity {
  company?: Company
  facilityId?: number | null
  type: string
  fridgeType?: FridgeType
  temperatureUpperLimit?: number | null
  temperatureLowerLimit?: number | null
}
export interface Zone extends Entity {
  number: number
  mode?: 'Off' | 'Active' | 'Heating' | 'Cooling' | 'Defrost' | 'Null' | 'Pretrip' | 'Inactive' | 'Sleep'
  setPointCelsius?: number
}

export interface MonitoringPoint extends Entity {
  company?: Company
  asset?: Asset
  zone: string | null
}

export interface AlarmCode {
  id: number
  code: number
  make: string
  fridgeMake: string
  description: string
  severityCode: number
  severityColour?: string
  severityId: 1 | 2 | 3
}

export interface Alarm {
  id: number
  company: Company
  asset: Asset
  alarmCode: AlarmCode
  severity: number
  code: string
  reeferStatus: string
  raisedTime: Date
  raisedLocation?: LatLng
  clearedTime?: Date
  clearedLocation?: LatLng
  raisedGeofence?: Geofence
  clearedGeofence?: Geofence
}

export interface AlertConfig {
  alertId: string
  alertName: string
  companyId?: number
  enabledAlertAcknowledgement?: boolean
}

export interface AlertInfo {
  key?: string
  description?: string
}

export interface AlertCustomDetails {
  fridgeAlarmInfo?: AlertInfo
  fuelPercentage?: string
  temperature?: number
  doorState?: string
  geofence?: string
}

export interface AlertData {
  id: number
  company: Company
  asset: Asset
  alertName: string
  severity: number
  raisedTime: Date
  raisedLocation?: LatLng
  clearedTime?: Date
  clearedLocation?: LatLng
  raisedGeofence?: Geofence
  clearedGeofence?: Geofence
  behavior?: string
  alertCustomDetails?: AlertCustomDetails
}

export interface AssetLocation {
  asset: Asset
  time: number
  longitude: number
  latitude: number
}

export interface MonitoringPointTemperature {
  company?: Company
  asset: Asset
  monitoringPoint: MonitoringPoint
  temperature: number
}

export interface DwellTime {
  entity: Entity
  days: number | string
}

/*
export interface Api {
  companies: (source: string, partialName?: string) => Promise<Company[]>;
  company: (source: string, id: string) => Promise<Company | null>;

  alarmCodes: (source: string) => Promise<AlarmCode[]>;
  alarmCodeForCode: (source: string, code: string) => Promise<AlarmCode | null>;
  alarmCode: (source: string, id: string) => Promise<AlarmCode | null>;
}
*/

export interface Config {
  [key: string]: string | number
}

export interface ConfigResponse {
  data: Config | undefined
  code: number
  error?: boolean
  message?: string
}

export interface ConfigObject {
  [configName: string]: {
    [key: string]: string | number
  }
}

export interface TaskType {
  id: number
  name: string
  description: string
}

export interface TaskCategory {
  id: number
  name: string
  description: string
}

export interface TaskPriority {
  id: number
  name: string
  description: string
}

export enum TaskStatus {
  Open,
  InProgress,
  Completed,
  Abandoned,
}

export interface BaseTask {
  id: number
  description: string
  createdAt: Date
  creator: string
  dueAt: Date
  /*
  assignee?: string | null;
  acknowledgedAt?: Date | null;
  completedAt?: Date | null;
  notes?: string | null;
  assignee?: string;
  acknowledgedAt?: Date;
  completedAt?: Date;
  notes?: string;
  */
  assignee: string | null
  acknowledgedAt: Date | null
  completedAt: Date | null
  notes: string | null
}

export interface DBTask extends BaseTask {
  task_name_id: number
  task_priority_id: number
  task_type_id: number
  status: string
}

export interface Task extends BaseTask {
  taskCategory: TaskCategory
  taskPriority: TaskPriority
  taskType: TaskType
  status: TaskStatus
}

export interface FlatAlarm {
  country: string
  umd: string
  make: string
  code: string
  asset: string
  raisedTime: string
}

export interface AssetMake {
  id: number
  make: string
  model: string
  critical: number
  informational: number
  warning: number
  total: number
}

export interface DoorOpenEvent extends Entity {
  asset: Asset
  monitoringPointId: number
  openTime: Date
  closeTime?: Date
  durationSeconds?: number
  location: LatLng
  address?: string
  sequence: number
}

export interface GeofenceDetails {
  id: number
  name: string
  addressStreet: string | null
  addressState: string | null
  addressPostcode: string | null
  addressCountry: string | null
  deleted: boolean
  companyId: bigint
  version: number
  addressSuburb: string | null
  zorder: number
  complexityTypeId: bigint | null
  northmostLatitudeDeg: number | null
  southmostLatitudeDeg: number | null
  westmostLongitudeDeg: number | null
  eastmostLongitudeDeg: number | null
  circleLatitudeDeg: number | null
  circleLongitudeDeg: number | null
  circleRadius: number | null
  visible: boolean
}

export interface GeofenceCategory {
  id: number
  name: string
}
